import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { EVENTS, formatTime } from '../../lib/vdot';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmt12(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getEventLabels(eventField) {
  if (!eventField) return '';
  try {
    const parsed = JSON.parse(eventField);
    if (Array.isArray(parsed)) return parsed.map(e => EVENTS[e]?.label ?? e).join(', ');
  } catch {}
  return EVENTS[eventField]?.label ?? eventField;
}

function isCallToday(schedule, today) {
  if (today.getDay() !== schedule.day_of_week) return false;
  if (schedule.frequency === 'weekly') return true;
  const ref = new Date(schedule.biweekly_ref_date + 'T00:00:00');
  const diffWeeks = Math.round((today - ref) / (1000 * 60 * 60 * 24 * 7));
  return Math.abs(diffWeeks) % 2 === 0;
}

export function CoachDashboard() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);
  const [activePlanCount, setActivePlanCount] = useState(0);
  const [unplandAthleteIds, setUnplannedAthleteIds] = useState([]);
  const [nextMonthUnplanned, setNextMonthUnplanned] = useState([]); // athletes missing next-month plan
  const [upcomingRaces, setUpcomingRaces] = useState([]);
  const [recentBenchmarks, setRecentBenchmarks] = useState([]);
  const [healthFlags, setHealthFlags] = useState([]);
  const [todayCalls, setTodayCalls] = useState([]); // { athleteName, call_time, timezone, frequency }
  const [planNotifications, setPlanNotifications] = useState([]); // athlete day-swap / rest-day alerts

  useEffect(() => {
    if (profile) { loadDashboard(); }
  }, [profile]);

  async function loadDashboard() {
    setLoading(true);

    // 1. Get all athletes under this coach
    const { data: aps } = await supabase
      .from('athlete_profiles')
      .select('id')
      .eq('coach_id', profile.id);

    if (!aps || aps.length === 0) {
      setLoading(false);
      return;
    }

    const athleteIds = aps.map(a => a.id);

    // 2. Get athlete names
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', athleteIds)
      .order('full_name');

    setAthletes(profiles ?? []);

    // Build a name lookup map
    const nameMap = {};
    (profiles ?? []).forEach(p => { nameMap[p.id] = p.full_name; });

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    // 3. Active plans this month — find which athletes have a plan
    const { data: assignments } = await supabase
      .from('athlete_plan_assignments')
      .select('athlete_id, plans(month, year, coach_id)')
      .in('athlete_id', athleteIds);

    const plannedIds = new Set(
      (assignments ?? [])
        .filter(a =>
          a.plans?.month === currentMonth &&
          a.plans?.year === currentYear &&
          a.plans?.coach_id === profile.id
        )
        .map(a => a.athlete_id)
    );

    setActivePlanCount(plannedIds.size);
    setUnplannedAthleteIds(athleteIds.filter(id => !plannedIds.has(id)));

    // Next-month alert: trigger in last 5 days of current month
    const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysLeft = daysInCurrentMonth - now.getDate(); // 0 = last day, 4 = 5 days from end
    if (daysLeft < 5) {
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
      const plannedNextIds = new Set(
        (assignments ?? [])
          .filter(a =>
            a.plans?.month === nextMonth &&
            a.plans?.year === nextYear &&
            a.plans?.coach_id === profile.id
          )
          .map(a => a.athlete_id)
      );
      setNextMonthUnplanned(athleteIds.filter(id => !plannedNextIds.has(id)).map(id => nameMap[id]).filter(Boolean));
    } else {
      setNextMonthUnplanned([]);
    }

    // 4. Upcoming races across all athletes
    const todayStr = now.toISOString().split('T')[0];
    const { data: races } = await supabase
      .from('races')
      .select('id, name, race_date, event, priority, athlete_id')
      .in('athlete_id', athleteIds)
      .gte('race_date', todayStr)
      .order('race_date', { ascending: true })
      .limit(8);

    setUpcomingRaces((races ?? []).map(r => ({ ...r, athleteName: nameMap[r.athlete_id] })));

    // 5. Recent race results — benchmarks with a race name (notes set, not baseline)
    const { data: raceResults } = await supabase
      .from('benchmarks')
      .select('id, athlete_id, event, time_seconds, race_date, notes')
      .in('athlete_id', athleteIds)
      .not('notes', 'is', null)
      .neq('notes', 'Baseline entry')
      .order('race_date', { ascending: false })
      .limit(6);

    setRecentBenchmarks((raceResults ?? []).map(r => ({
      ...r,
      athleteName: nameMap[r.athlete_id],
    })));

    // 6. Active health flags (unresolved)
    const { data: flags } = await supabase
      .from('health_flags')
      .select('id, athlete_id, flag_date, type, body_part, pain_scale, illness_type, notes, created_at')
      .in('athlete_id', athleteIds)
      .eq('resolved', false)
      .order('created_at', { ascending: false })
      .limit(20);

    setHealthFlags((flags ?? []).map(f => ({ ...f, athleteName: nameMap[f.athlete_id] })));

    // 7. Today's coaching calls
    const { data: callSchedules } = await supabase
      .from('coaching_call_schedules')
      .select('athlete_id, day_of_week, call_time, timezone, frequency, biweekly_ref_date')
      .eq('coach_id', profile.id)
      .eq('active', true);

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    setTodayCalls(
      (callSchedules ?? [])
        .filter(s => isCallToday(s, todayMidnight))
        .map(s => ({ ...s, athleteName: nameMap[s.athlete_id] ?? 'Athlete' }))
        .sort((a, b) => a.call_time.localeCompare(b.call_time))
    );

    // 8. Athlete plan change notifications (unreviewed day swaps / rest days)
    const { data: notifs } = await supabase
      .from('athlete_plan_notifications')
      .select('id, athlete_id, type, original_date, new_date, message, created_at')
      .in('athlete_id', athleteIds)
      .eq('coach_reviewed', false)
      .order('created_at', { ascending: false })
      .limit(30);

    setPlanNotifications((notifs ?? []).map(n => ({ ...n, athleteName: nameMap[n.athlete_id] ?? 'Athlete' })));

    setLoading(false);
  }

  async function markNotifReviewed(id) {
    await supabase.from('athlete_plan_notifications').update({ coach_reviewed: true }).eq('id', id);
    setPlanNotifications(prev => prev.filter(n => n.id !== id));
  }

  async function markFlagResolved(flagId) {
    await supabase
      .from('health_flags')
      .update({ resolved: true, resolved_at: new Date().toISOString() })
      .eq('id', flagId);
    setHealthFlags(prev => prev.filter(f => f.id !== flagId));
  }

  const now = new Date();
  const currentMonthLabel = MONTHS[now.getMonth()];
  const currentYear = now.getFullYear();

  // Names of unplanned athletes (for display)
  const nameMap = {};
  athletes.forEach(a => { nameMap[a.id] = a.full_name; });
  const unplannedNames = unplandAthleteIds.map(id => nameMap[id]).filter(Boolean);

  return (
    <Layout>
      <div style={{ marginBottom: 28 }}>
        <p className="page-eyebrow">Coach Dashboard</p>
        <h1 className="page-title">Welcome back, {profile?.full_name?.split(' ')[0]}.</h1>
      </div>

      {/* Today's calls reminder */}
      {!loading && todayCalls.length > 0 && (
        <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--navy)' }}>
          <div className="section-card-header">
            <span className="section-card-title" style={{ color: 'var(--navy)' }}>
              📞 Calls Today ({todayCalls.length})
            </span>
            <Link to="/coach/calls" style={{ fontSize: 12, color: 'var(--navy)', textDecoration: 'none', fontWeight: 600 }}>
              Open Calls →
            </Link>
          </div>
          <div style={{ padding: '6px 0 12px' }}>
            {todayCalls.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 24px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', minWidth: 120 }}>
                  {fmt12(c.call_time)}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                  {c.athleteName}
                  <span style={{ color: 'var(--gray-400)', fontSize: 11, marginLeft: 6 }}>
                    {c.frequency === 'biweekly' ? 'biweekly' : 'weekly'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-label">Athletes</p>
          <p className="stat-value">{loading ? '—' : athletes.length}</p>
          <p className="stat-sub">On your roster</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Active Plans</p>
          <p className="stat-value">{loading ? '—' : activePlanCount}</p>
          <p className="stat-sub">{currentMonthLabel} {currentYear}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Upcoming Races</p>
          <p className="stat-value">{loading ? '—' : upcomingRaces.length}</p>
          <p className="stat-sub">Across all athletes</p>
        </div>
      </div>

      {/* No athletes yet */}
      {!loading && athletes.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-title">No athletes on your roster yet</p>
          <p className="empty-state-sub">
            <Link to="/coach/athletes" style={{ color: 'var(--gold)', fontWeight: 600 }}>Go to Athletes</Link> to add your first athlete.
          </p>
        </div>
      )}

      {/* Unplanned athletes alert */}
      {!loading && unplannedNames.length > 0 && (
        <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--gold)' }}>
          <div className="section-card-header">
            <span className="section-card-title">Missing Plans — {currentMonthLabel}</span>
            <Link to="/coach/plan-builder" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none', fontWeight: 600 }}>
              Open Plan Builder →
            </Link>
          </div>
          <div style={{ padding: '12px 24px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {unplannedNames.map(name => (
              <span key={name} style={{ fontSize: 13, background: 'var(--gray-100)', borderRadius: 6, padding: '4px 10px', color: 'var(--gray-600)' }}>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Next-month plan alert — shown in last 5 days of month */}
      {!loading && nextMonthUnplanned.length > 0 && (() => {
        const now = new Date();
        const nm = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
        const nextMonthLabel = MONTHS[nm];
        return (
          <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid #f59e0b' }}>
            <div className="section-card-header">
              <span className="section-card-title" style={{ color: '#92400e' }}>
                {nextMonthLabel} Plans Outstanding
              </span>
              <Link to="/coach/plan-builder" style={{ fontSize: 12, color: '#f59e0b', textDecoration: 'none', fontWeight: 600 }}>
                Open Plan Builder →
              </Link>
            </div>
            <div style={{ padding: '4px 24px 12px', fontSize: 13, color: '#78350f' }}>
              {nextMonthUnplanned.length === 1
                ? `${nextMonthUnplanned[0]} doesn't have a plan for ${nextMonthLabel} yet.`
                : `${nextMonthUnplanned.length} athletes don't have ${nextMonthLabel} plans yet.`}
            </div>
            <div style={{ padding: '0 24px 12px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {nextMonthUnplanned.map(name => (
                <span key={name} style={{ fontSize: 13, background: '#fef3c7', borderRadius: 6, padding: '4px 10px', color: '#92400e' }}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Athlete plan change notifications */}
      {!loading && planNotifications.length > 0 && (
        <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--navy)' }}>
          <div className="section-card-header">
            <span className="section-card-title" style={{ color: 'var(--navy)' }}>
              Plan Changes ({planNotifications.length})
            </span>
          </div>
          <div className="plan-notif-list">
            {planNotifications.map(n => (
              <div key={n.id} className="plan-notif-item">
                <div className="plan-notif-icon">
                  {n.type === 'day_swap' ? '↔' : '🛌'}
                </div>
                <div className="plan-notif-info">
                  <div className="plan-notif-athlete">{n.athleteName}</div>
                  <div className="plan-notif-msg">{n.message}</div>
                  <div className="plan-notif-date">
                    {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
                <button className="plan-notif-ack-btn" onClick={() => markNotifReviewed(n.id)}>
                  Got it
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Health alerts */}
      {!loading && healthFlags.length > 0 && (
        <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid #dc2626' }}>
          <div className="section-card-header">
            <span className="section-card-title" style={{ color: '#dc2626' }}>
              Health Alerts ({healthFlags.length})
            </span>
          </div>
          <div className="health-flag-list">
            {healthFlags.map(flag => (
              <div key={flag.id} className="health-flag-item">
                <div className={`health-flag-badge health-flag-badge--${flag.type}`}>
                  {flag.type === 'injury' ? '🦵' : '🤒'}
                </div>
                <div className="health-flag-info">
                  <div className="health-flag-athlete">{flag.athleteName}</div>
                  <div className="health-flag-detail">
                    {flag.type === 'injury'
                      ? `${flag.body_part ?? 'Unknown area'}${flag.pain_scale ? ` · Pain ${flag.pain_scale}/10` : ''}`
                      : flag.illness_type ?? 'Illness'}
                  </div>
                  {flag.notes && <div className="health-flag-notes">"{flag.notes}"</div>}
                  <div className="health-flag-date">{flag.flag_date}</div>
                </div>
                <button
                  className="health-flag-resolve-btn"
                  onClick={() => markFlagResolved(flag.id)}
                >
                  Mark Resolved
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && athletes.length > 0 && (
        <div className="profile-grid">
          {/* Upcoming Races */}
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Upcoming Races</span>
            </div>
            {upcomingRaces.length === 0 ? (
              <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                No upcoming races across your roster.
              </div>
            ) : (
              <div className="race-list">
                {upcomingRaces.map(race => {
                  const days = daysUntil(race.race_date);
                  return (
                    <div key={race.id} className="race-item">
                      <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                      <div className="race-item-main">
                        <div className="race-item-name">{race.name}</div>
                        <div className="race-item-meta">
                          {race.athleteName} · {getEventLabels(race.event)}
                        </div>
                      </div>
                      <div className="race-item-right">
                        <div className="race-item-date">{formatRaceDate(race.race_date)}</div>
                        <div className={`race-countdown${days <= 7 ? ' race-countdown--soon' : ''}`}>
                          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Race Results */}
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Recent Race Results</span>
            </div>
            {recentBenchmarks.length === 0 ? (
              <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                No race results logged yet.
              </div>
            ) : (
              <div className="benchmark-list" style={{ marginTop: 0, padding: '0 8px' }}>
                {recentBenchmarks.map(b => (
                  <div key={b.id} className="benchmark-item">
                    <div>
                      <span className="benchmark-item-event">{getEventLabels(b.event)}</span>
                      <div className="benchmark-item-date" style={{ marginTop: 2 }}>
                        {b.athleteName}
                        {b.race_date && ` · ${b.race_date}`}
                      </div>
                      {b.notes && <div className="benchmark-item-date">{b.notes}</div>}
                    </div>
                    <span className="benchmark-item-time">{formatTime(b.time_seconds)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </Layout>
  );
}
